// src/services/subscriptionService.js
import { USER_TIERS } from './UserService';
import { doc, getDoc } from "firebase/firestore";
import { db } from '../firebase/firebaseConfig';

// Local storage keys
const STORAGE_KEYS = {
  PREMIUM_STATUS: 'pilatesPulse_premium', // Kept for backward compatibility
  PRO_STATUS: 'pilatesPulse_pro', // New key
  SUBSCRIPTION_DATA: 'pilatesPulse_subscription'
};

/**
 * Check if user has an active pro subscription
 * @returns {boolean} Whether user has pro access
 */
export const isPremiumUser = () => {
  try {
    // Check local storage for premium/pro status
    const hasProStatus = localStorage.getItem(STORAGE_KEYS.PRO_STATUS) === 'true';
    const hasPremiumStatus = localStorage.getItem(STORAGE_KEYS.PREMIUM_STATUS) === 'true';
    
    return hasProStatus || hasPremiumStatus;
  } catch (error) {
    console.error('Error checking pro status:', error);
    return false;
  }
};

/**
 * Get user's subscription data
 * @returns {Object|null} Subscription data if available
 */
export const getSubscriptionData = () => {
  try {
    const subscriptionJson = localStorage.getItem(STORAGE_KEYS.SUBSCRIPTION_DATA);
    return subscriptionJson ? JSON.parse(subscriptionJson) : null;
  } catch (error) {
    console.error('Error getting subscription data:', error);
    return null;
  }
};

/**
 * Get user's subscription from Firestore by user ID
 * @param {string} userId - The user ID to get subscription for
 * @returns {Promise<Object|null>} Subscription data if available
 */
export const getSubscription = async (userId) => {
  try {
    if (!userId) return null;
    
    // First check local storage for cached data
    const cachedData = getSubscriptionData();
    if (cachedData) return cachedData;
    
    // If not in local storage, fetch from Firestore
    const userDocRef = doc(db, "users", userId);
    
    // Get the document snapshot
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData.subscription) {
        const subscriptionData = userData.subscription;
        
        // Cache the subscription data in local storage
        updateSubscription(subscriptionData);
        
        return subscriptionData;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching subscription:', error);
    return null;
  }
};

/**
 * Update subscription data and set pro status
 * @param {Object} subscriptionData - The subscription data to save
 */
export const updateSubscription = (subscriptionData) => {
  try {
    // Save subscription data
    localStorage.setItem(STORAGE_KEYS.SUBSCRIPTION_DATA, JSON.stringify(subscriptionData));
    
    // Set pro status to true
    localStorage.setItem(STORAGE_KEYS.PRO_STATUS, 'true');
    
    // For backward compatibility
    localStorage.setItem(STORAGE_KEYS.PREMIUM_STATUS, 'true');
    
    // Dispatch event for components to update
    window.dispatchEvent(new CustomEvent('proStatusChanged', { detail: { isPro: true } }));
    
    return true;
  } catch (error) {
    console.error('Error updating subscription:', error);
    return false;
  }
};

/**
 * Revoke subscription and pro status
 */
export const revokeSubscription = () => {
  try {
    // Remove pro status
    localStorage.setItem(STORAGE_KEYS.PRO_STATUS, 'false');
    localStorage.setItem(STORAGE_KEYS.PREMIUM_STATUS, 'false');
    
    // Clear subscription data
    localStorage.removeItem(STORAGE_KEYS.SUBSCRIPTION_DATA);
    
    // Dispatch event for components to update
    window.dispatchEvent(new CustomEvent('proStatusChanged', { detail: { isPro: false } }));
    
    return true;
  } catch (error) {
    console.error('Error revoking subscription:', error);
    return false;
  }
};

/**
 * Get days remaining in current subscription
 * @returns {number} Days remaining, or 0 if not pro
 */
export const getDaysRemaining = () => {
  const subscriptionData = getSubscriptionData();
  
  if (!subscriptionData || !subscriptionData.currentPeriodEnd) {
    return 0;
  }
  
  const endDate = new Date(subscriptionData.currentPeriodEnd);
  const currentDate = new Date();
  
  // If already expired
  if (currentDate > endDate) {
    return 0;
  }
  
  // Calculate days remaining
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysRemaining = Math.ceil((endDate - currentDate) / msPerDay);
  
  return daysRemaining;
};

const subscriptionService = {
  isPremiumUser,
  getSubscriptionData,
  getSubscription,
  updateSubscription,
  revokeSubscription,
  getDaysRemaining
};

export default subscriptionService;